import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
  FiFileText, FiTrash2, FiAlertCircle,
  FiThumbsUp, FiThumbsDown, FiCopy, FiRefreshCw,
} from 'react-icons/fi';
import { handleUnauthorized } from '../../utils/apiClient';
import { useToast } from '../../context/ToastContext';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import './PolicyComparison.css';


const getToken = (): string => localStorage.getItem('toolboxToken') || '';

interface FeedbackState {
  text: string;
  rating: boolean | null;
  error: string;
  submitting: boolean;
  success: boolean;
  anchor: { bottom: number; right: number } | null;
}

interface Props {
  onGoToHistory?: () => void;
}

const PolicySummaryUpload: React.FC<Props> = ({ onGoToHistory }) => {
  const { showSuccess, showWarning, showError } = useToast();
  /* ── file ── */
  const [file1, setFile1] = useState<File | null>(null);
  const [drag1, setDrag1] = useState(false);
  const [fileErr1, setFileErr1] = useState('');
  const fileRef1 = useRef<HTMLInputElement>(null);

  /* ── balance ── */
  const [insufficientBalance] = useState(false);

  /* ── streaming response ── */
  const [response, setResponse]         = useState('');
  const [streaming, setStreaming]       = useState(false);
  const [activeAction, setActiveAction] = useState<number | null>(null);
  const [feedbackId, setFeedbackId]     = useState('');
  const ctrl = useRef<AbortController | null>(null);

  /* ── feedback ── */
  const [fb, setFb] = useState<FeedbackState>({
    text: '', rating: null, error: '', submitting: false, success: false, anchor: null,
  });
  const feedbackRef = useRef<HTMLDivElement>(null);

  // useEffect(() => { fetchBalance(); }, [fetchBalance]);

  /* ── close feedback on outside click ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        setFb(p => ({ ...p, anchor: null }));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const acceptFile1 = (f: File) => {
    if (f.type !== 'application/pdf') { setFileErr1('Only PDF files are accepted.'); return; }
    setFileErr1(''); setFile1(f);
  };

  const validate = (): boolean => {
    if (!file1) { showWarning('Please select a policy PDF to continue.'); return false; }
    return true;
  };

  const getPrompt = (prompt: number): string => {
    const n1 = file1?.name ?? 'Policy';
    if (prompt === 1) return `
**📄 Provide a structured and detailed summary of ${n1}. Extract all key fields along with their values, ensuring completeness and accuracy. The summary should include:**

---

## 1️⃣ General Policy Information
- **Policy Number**
- **Insured Details** (Name and Address)
- **Policy Term** (Effective & Expiration Date)
- **Agent Details** (Name, Contact Information)
- **Insurance Carrier Details** (Name and NAICS)

---

## 2️⃣ Coverage Details with Limits
- List all **coverages provided** in the policy along with:
  - **Coverage Name**
  - **Coverage Limit**
  - **Deductible (if any)**
  - **Conditions or Special Notes**

---

## 3️⃣ Premium Breakdown
- Provide a structured breakdown of the **total premium**, including:
  - **Individual coverage costs**
  - **Any additional fees, discounts, or surcharges**

---

## 4️⃣ Insured Vehicles or Assets (If Applicable)
- For each insured vehicle or asset: **Year, Make, Model, VIN, Associated Coverages & Limits, Deductibles**

---

## 5️⃣ Drivers Information (If Applicable)
- For each driver: **Name, License Number, Date of Birth, Relationship to Insured, Driving History**

---

## 6️⃣ Endorsements, Exclusions & Special Conditions
- Summarize any special endorsements, exclusions, or modifications affecting the policy.

---

## 7️⃣ Policy Terms & Conditions Summary
- Extract and summarize any specific terms, conditions, cancellation policies, or legal notices.

---

**📊 Tabular Format** – Present extracted coverages, limits, premiums, and deductibles in a structured table for easy reference.
**🎯 Throughout your response, always include insurance-related standard emojis.**`;

    if (prompt === 5) return `You are an AI assistant that specializes in generating professional insurance policy proposals. Your input is a quote letter from an insurance company that includes the client details, policy terms, premium amounts, coverage details, exclusions, endorsements, and other relevant information. Generate a well-structured, professional insurance proposal clearly outlining the key aspects of the policy in a client-friendly format. Include: Title, Introduction, Client Details, Policy Details, Coverage Details, Exclusions, Terms and Conditions, Benefits, Next Steps, and a professional Closing Statement.`;
    return '';
  };

  const getAction = (prompt: number): string => {
    const map: Record<number, string> = { 1: 'Summarize Policy', 5: 'Generate Proposal' };
    return map[prompt] ?? '';
  };

  const run = async (prompt: number) => {
    if (!validate()) return;
    if (ctrl.current) ctrl.current.abort();
    ctrl.current = new AbortController();

    setActiveAction(prompt);
    setResponse('');
    setFeedbackId('');
    setStreaming(false);

    const formData = new FormData();
    if (file1) formData.append('FirstFormFile', file1);
    formData.append('Question', getPrompt(prompt));
    formData.append('Action', getAction(prompt));

    try {
      await fetchEventSource(`${(process.env.REACT_APP_PolicyAgent_Url      || '')}/process_summarize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
        signal: ctrl.current.signal,
        onopen: async (res: Response) => {
          if (res.ok && res.headers.get('content-type') === 'text/event-stream') return;
          if (res.status === 401) { handleUnauthorized(); return; }
          if (res.status === 403) showWarning('Insufficient credits to process this request.');
          setActiveAction(null); setStreaming(false);
        },
        onmessage: (event: { data: string; event?: string; id?: string; retry?: number }) => {
          try {
            const parsed = JSON.parse(event.data);
            if (parsed.transaction_id) setFeedbackId(parsed.transaction_id);
            if (parsed.response) {
              setStreaming(true);
              setActiveAction(null);
              setResponse(prev => prev + parsed.response);
            } else {
              setResponse(prev => prev + '\n');
            }
          } catch { /* non-JSON event */ }
        },
        onclose: () => {
          setStreaming(false);
          // fetchBalance();
        },
        onerror: () => {
          setStreaming(false);
          setActiveAction(null);
        },
      });
    } catch { /* aborted */ }
  };

  const reset = () => {
    if (ctrl.current) ctrl.current.abort();
    setResponse(''); setStreaming(false); setActiveAction(null); setFeedbackId('');
  };

  const copyToClipboard = async () => {
    try {
      const html = await marked(response) as string;
      const styledHtml = applyTableStyles(html);
      const plain = htmlToPlain(html);
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([styledHtml], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      showSuccess('Copied to clipboard!');
    } catch {
      showError('Failed to copy to clipboard.');
    }
  };

  const applyTableStyles = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('table').forEach(t => {
      t.setAttribute('style', 'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;');
      t.querySelectorAll('th').forEach(th => th.setAttribute('style', 'border:2px solid #e3e3e3;padding:8px 10px;'));
      t.querySelectorAll('td').forEach(td => td.setAttribute('style', 'border:1px solid #e3e3e3;padding:7px 10px;'));
    });
    return div.innerHTML;
  };

  const htmlToPlain = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('br, p, h1, h2, h3, h4, h5, h6').forEach(el => {
      el.parentNode?.insertBefore(document.createTextNode('\n'), el);
    });
    return div.textContent || div.innerText || '';
  };

  const openFeedback = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setFb(p => ({ ...p, anchor: { bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right }, error: '', success: false }));
  };

  const onSaveRatings = useCallback((isLiked: boolean | null, feedbackText: string) => {
    if (!feedbackId) return;
    axios.post(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/OCR/policycomparisonratings`,
      { TransactionId: feedbackId, IsLiked: isLiked, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      showSuccess('Feedback submitted successfully!');
      setFb(p => ({ ...p, anchor: null, text: '', rating: null, submitting: false }));
    }).catch(err => {
      if (err.response?.status === 401) handleUnauthorized();
    });
  }, [feedbackId, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    const next: boolean | null = fb.rating === true ? null : true;
    setFb(p => ({ ...p, anchor: null }));
    onSaveRatings(next, fb.text);
  }, [fb.rating, fb.text, onSaveRatings]);

  const onThumbsDownClick = useCallback(() => {
    const next: boolean | null = fb.rating === false ? null : false;
    setFb(p => ({ ...p, anchor: null }));
    onSaveRatings(next, fb.text);
  }, [fb.rating, fb.text, onSaveRatings]);

  const onSubmitFeedback = useCallback(() => {
    if (!fb.text.trim()) { setFb(p => ({ ...p, error: 'Please enter your feedback.' })); return; }
    if (fb.text.length > 250) { setFb(p => ({ ...p, error: 'Feedback must be under 250 characters.' })); return; }
    if (!feedbackId) { showError('No transaction to attach feedback to.'); return; }
    setFb(p => ({ ...p, submitting: true, error: '' }));
    onSaveRatings(fb.rating, fb.text);
  }, [fb.text, fb.rating, feedbackId, showError, onSaveRatings]);

  const isLoading = activeAction !== null;

  return (
    <div className="cs-upload-container">
      {insufficientBalance && (
        <div className="pc-insufficient-banner">
          <FiAlertCircle size={15} />
          Insufficient balance. Please top up your account to use Policy Summary.
        </div>
      )}

      {/* ── File zone ── */}
      <div style={{ marginTop: 8 }}>
        <div className="pc-file-zone">
          <div
            className={`cs-dropzone${drag1 ? ' cs-dropzone--dragging' : ''}`}
            onDrop={e => { e.preventDefault(); setDrag1(false); const f = e.dataTransfer.files[0]; if (f) acceptFile1(f); }}
            onDragOver={e => { e.preventDefault(); setDrag1(true); }}
            onDragLeave={() => setDrag1(false)}
          >
            <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
            <p className="cs-dropzone__title">Policy Document</p>
            <p className="cs-dropzone__or">Drag & Drop your file here</p>
            <p className="cs-dropzone__or">or</p>
            <button
              type="button"
              className="cs-browse-btn"
              onClick={() => fileRef1.current?.click()}
            >
              Browse Files
            </button>
            <p className="cs-dropzone__types">Allowed file types: <strong>PDF</strong></p>
            {file1 && (
              <div className="cs-file-card" style={{ marginTop: 10, width: '100%' }}>
                <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                <div className="cs-file-card__info">
                  <span className="cs-file-card__name">{file1.name}</span>
                  <span className="cs-file-card__size">{(file1.size / 1024).toFixed(0)} KB</span>
                </div>
                <button className="cs-file-card__delete" onClick={() => { setFile1(null); reset(); }} title="Remove file">
                  <FiTrash2 size={15} />
                </button>
              </div>
            )}
            {fileErr1 && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{fileErr1}</div>}
            <input ref={fileRef1} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile1(f); e.target.value = ''; }} />
          </div>
          <div className="cs-info-banner">
            <FiAlertCircle size={14} />
            <span>
              This tool works best with readable PDFs. Please note that scanned or
              image-based PDFs may not be processed. For scanned documents, consider
              converting them to a readable format using OCR tools before uploading.
            </span>
          </div>
        </div>
      </div>

      {/* ── Action buttons ── */}
      <div className="pc-actions">
        <button
          className={`pc-action-btn${activeAction === 1 ? ' pc-action-btn--active' : ''}`}
          disabled={isLoading || insufficientBalance}
          onClick={() => run(1)}
        >
          {activeAction === 1 ? <span className="pc-spinner" /> : null}
          Summarize Policy
        </button>
        <button
          className={`pc-action-btn${activeAction === 5 ? ' pc-action-btn--active' : ''}`}
          disabled={isLoading || insufficientBalance}
          onClick={() => run(5)}
        >
          {activeAction === 5 ? <span className="pc-spinner" /> : null}
          Generate Proposal
        </button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
        AI-generated output — verify important information before use.
      </div>

      {/* ── Streaming response ── */}
      {(response || streaming) && (
        <div className="pc-response-card">
          <div className="pc-markdown-body">
            <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
              {response}
            </ReactMarkdown>
            {streaming && <span className="pc-streaming-cursor" />}
          </div>

          {!streaming && (
            <div className="pc-response-card__toolbar">
              <button className="pc-response-card__toolbar-btn" onClick={reset}>
                <FiRefreshCw size={12} /> Try Again
              </button>
              <button className="pc-response-card__toolbar-btn" onClick={copyToClipboard}>
                <FiCopy size={12} /> Copy
              </button>
              {feedbackId && (
                <button className="pc-response-card__toolbar-btn" onClick={openFeedback}>
                  Feedback
                </button>
              )}
              {onGoToHistory && (
                <button className="pc-response-card__toolbar-btn" onClick={onGoToHistory}>
                  View History
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Feedback popup (portalled) ── */}
      {fb.anchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ bottom: fb.anchor.bottom, right: fb.anchor.right, top: 'auto' }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            rows={4}
            placeholder="Enter your feedback here…"
            value={fb.text}
            onChange={e => setFb(p => ({ ...p, text: e.target.value, error: '' }))}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${fb.rating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Helpful"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${fb.rating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Not helpful"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {fb.error && <p className="csfb-error">{fb.error}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={() => setFb(p => ({ ...p, anchor: null }))}>
              Cancel
            </button>
            <button
              className="csfb-btn-submit"
              disabled={fb.submitting}
              onClick={onSubmitFeedback}
            >
              {fb.submitting ? '…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PolicySummaryUpload;

